import { Link } from 'react-router-dom'

import Container from '@/components/ui/Container'
import { useData } from '@/hooks/useLocale'

const number = (i) => String(i + 1).padStart(2, '0')

const EMAIL = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/

/** Text with every email address turned into a mailto link. */
function Linked({ text }) {
  return text.split(EMAIL).map((part, i) =>
    i % 2 ? (
      <a
        key={i}
        href={`mailto:${part}`}
        className="text-brand hover:text-brand-dark"
      >
        {part}
      </a>
    ) : (
      part
    ),
  )
}

/** One block of a section's body: a paragraph, a bold-led paragraph, a list or a table. */
function Block({ block }) {
  if (typeof block === 'string')
    return (
      <p>
        <Linked text={block} />
      </p>
    )
  if (block.lead)
    return (
      <p>
        <strong className="font-medium text-ink">{block.lead}</strong>{' '}
        <Linked text={block.text} />
      </p>
    )
  if (block.list)
    return (
      <ul className="flex list-disc flex-col gap-1.5 ps-5">
        {block.list.map((item) => (
          <li key={item}>
            <Linked text={item} />
          </li>
        ))}
      </ul>
    )
  return (
    <div className="overflow-x-auto rounded-2xl border border-line">
      <table className="w-full border-collapse text-start text-sm/[1.6]">
        <thead>
          <tr>
            {block.table.head.map((cell) => (
              <th
                key={cell}
                className="min-w-32 bg-surface px-3.5 py-2.5 text-start align-top font-medium text-ink"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.table.rows.map((row) => (
            <tr key={row.join()} className="border-t border-line">
              {row.map((cell, i) => (
                <td key={i} className="min-w-32 px-3.5 py-2.5 align-top">
                  <Linked text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * A legal document's numbered sections, beside a contents list (sticky from
 * 900px up) that also links to the other documents.
 */
export default function LegalDocumentSection({ doc }) {
  const { LEGAL_DOCS, LEGAL_META } = useData()
  const related = LEGAL_DOCS.filter((other) => other.path !== doc.path)

  return (
    <section className="flex-1 py-14">
      <Container className="flex flex-wrap items-start gap-14">
        <nav
          aria-label={LEGAL_META.tocTitle}
          className="flex flex-[1_1_100%] flex-col gap-0.5 rounded-[20px] bg-surface p-3.5 md:sticky md:top-29 md:flex-[0_0_240px]"
        >
          <span className="px-2.5 pt-1.5 pb-2.5 text-xs font-medium tracking-[.12em] text-subtle uppercase">
            {LEGAL_META.tocTitle}
          </span>
          {doc.sections.map((section, i) => (
            <a
              key={section.title}
              href={`#${section.id}`}
              className="flex gap-2.5 rounded-[10px] px-2.5 py-[9px] text-sm text-body hover:bg-white hover:text-brand"
            >
              <span className="w-[18px] text-[#a3a9b5] tabular-nums">
                {number(i)}
              </span>
              {section.title}
            </a>
          ))}
          <div className="m-2.5 h-px bg-[#e3e7ee]" />
          {related.map((other) => (
            <Link
              key={other.path}
              to={other.path}
              className="rounded-[10px] px-2.5 py-2 text-[13px] text-muted hover:bg-white hover:text-brand"
            >
              {other.title} <span className="inline-block rtl:-scale-x-100">→</span>
            </Link>
          ))}
        </nav>

        <div className="flex max-w-190 min-w-0 flex-[1_1_420px] flex-col">
          {doc.sections.map((section, i) => (
            <div
              key={section.title}
              id={section.id}
              className="flex scroll-mt-7 flex-col gap-2.5 border-t border-line py-7 first:border-t-0 first:pt-1"
            >
              <h2 className="flex items-baseline gap-3.5 text-[22px] font-medium tracking-[-0.01em]">
                <span className="text-sm text-brand tabular-nums">{number(i)}</span>
                {section.title}
              </h2>
              <div className="flex min-w-0 flex-col gap-3 ps-8 text-base/[1.75] text-pretty text-body">
                {section.body.map((block, b) => (
                  <Block key={b} block={block} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}
